import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  TrendingUp, Dumbbell, Utensils, PlaySquare, Heart,
  ArrowRight, Lock, Play, User, Check,
} from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { useCheckout } from '../CheckoutContext';

const API = process.env.REACT_APP_BACKEND_URL;
const VIDEO_URL = {
  fr: '/openbro-motion-fr.mp4',
  en: '/openbro-motion.mp4',
};

const COPY = {
  fr: {
    nav: ['BUSINESS', 'SPORT', 'FOOD', 'IA', 'VIDÉO', 'RÉSEAUX', 'E-COMMERCE', 'RELATIONSHIP'],
    member: 'ACCÈS MEMBRE',
    eyebrow: 'OPENBRO',
    tagline: 'A BRIGHTER\nTOMORROW.',
    centerTop: 'PEOPLE\nTECHNOLOGY\nOPPORTUNITY\nA BRIGHTER\nTOMORROW.',
    centerBottom: 'THE NEXT\nGENERATION\nPROFESSIONAL\nPLATFORM.',
    features: [
      'Un appel privé par mois.',
      'Un plan pour 30 jours.',
      'Tous tes sujets.',
      'Une communauté privée.',
      '6 formations incluses.',
    ],
    members: 'Sans engagement',
    values: ['DISCIPLINE', 'IDÉES', 'ACTION', 'RÉSULTATS'],
    kicker: '1 APPEL PRIVÉ PAR MOIS',
    period: '/mois',
    perks: [
      'Un appel privé avec moi chaque mois',
      "Ton plan d'action 30 jours",
      '6 formations, 100+ leçons',
      'La communauté privée',
      'Résiliable à tout moment',
    ],
    cta: 'COMMENCER MAINTENANT',
    secure: 'Paiement sécurisé',
    quote: '" SAME\nQUESTIONS.\nA BRIGHTER\nYOU. "',
    video: 'VOIR LA VIDÉO',
    accel: "PLUS\nQU'UN APPEL.\nUN ACCÉLÉRATEUR.",
    prompt: "Code d'accès :",
    invalid: 'Code invalide',
  },
  en: {
    nav: ['BUSINESS', 'SPORT', 'FOOD', 'AI', 'VIDEO', 'NETWORKS', 'E-COMMERCE', 'RELATIONSHIP'],
    member: 'MEMBER ACCESS',
    eyebrow: 'OPENBRO',
    tagline: 'A BRIGHTER\nTOMORROW.',
    centerTop: 'PEOPLE\nTECHNOLOGY\nOPPORTUNITY\nA BRIGHTER\nTOMORROW.',
    centerBottom: 'THE NEXT\nGENERATION\nPROFESSIONAL\nPLATFORM.',
    features: [
      'One private call a month.',
      'A 30-day plan.',
      'Every topic.',
      'A private community.',
      '6 programs included.',
    ],
    members: 'No commitment',
    values: ['DISCIPLINE', 'IDEAS', 'ACTION', 'RESULTS'],
    kicker: '1 PRIVATE CALL A MONTH',
    period: '/month',
    perks: [
      'A private call with me every month',
      'Your 30-day action plan',
      '6 programs, 100+ lessons',
      'The private community',
      'Cancel anytime',
    ],
    cta: 'START NOW',
    secure: 'Secure payment',
    quote: '" SAME\nQUESTIONS.\nA BRIGHTER\nYOU. "',
    video: 'WATCH THE VIDEO',
    accel: 'MORE THAN\nA CALL.\nAN ACCELERATOR.',
    prompt: 'Access code:',
    invalid: 'Invalid code',
  },
};

const FEATURE_ICONS = [TrendingUp, Dumbbell, Utensils, PlaySquare, Heart];
const AVATARS = [
  '/avatars/member-1.jpg',
  '/avatars/member-2.jpg',
  '/avatars/member-3.jpg',
  '/avatars/member-4.jpg',
  '/avatars/member-5.jpg',
];

// 1 mockup pixel (1024x576 design) expressed in the responsive unit --s
const u = (n) => `calc(${n} * var(--s))`;

const Lines = ({ text }) => text.split('\n').map((l, i) => (
  <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>
));

// Glass rim: gradient stroke bright at the top-left, fading, with small light bursts at the corners
const Rim = ({ id, r }) => (
  <svg className="ob-rim" aria-hidden="true">
    <defs>
      <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#fff" stopOpacity="1" />
        <stop offset="0.22" stopColor="#fff" stopOpacity="0.55" />
        <stop offset="0.5" stopColor="#fff" stopOpacity="0.08" />
        <stop offset="0.78" stopColor="#fff" stopOpacity="0.12" />
        <stop offset="1" stopColor="#fff" stopOpacity="0.7" />
      </linearGradient>
    </defs>
    <rect className="ob-rim-rect" style={{ rx: r, ry: r }} fill="none" stroke={`url(#${id}-g)`} strokeWidth="1.8" />
  </svg>
);

const AppleGlyph = () => (
  <svg viewBox="0 0 170 170" aria-hidden="true">
    <path fill="currentColor" d="M150.4 130.3c-2.5 5.7-5.4 11-8.8 15.8-4.6 6.6-8.4 11.1-11.3 13.6-4.5 4.2-9.4 6.3-14.6 6.4-3.7 0-8.2-1.1-13.5-3.2-5.3-2.1-10.1-3.2-14.6-3.2-4.7 0-9.7 1.1-15 3.2-5.3 2.2-9.6 3.3-12.9 3.4-5 .2-10-2-15-6.6-3.2-2.8-7.1-7.5-11.8-14.1-5-7.1-9.2-15.3-12.4-24.7-3.5-10.1-5.2-20-5.2-29.5 0-10.9 2.4-20.3 7.1-28.2 3.7-6.3 8.6-11.3 14.8-15 6.2-3.7 12.9-5.5 20.1-5.7 3.9 0 9.1 1.2 15.5 3.6 6.4 2.4 10.5 3.6 12.3 3.6 1.3 0 5.9-1.4 13.6-4.2 7.3-2.6 13.5-3.7 18.5-3.3 13.7 1.1 24 6.5 30.8 16.3-12.2 7.4-18.3 17.8-18.2 31.1.1 10.4 3.9 19 11.3 25.9 3.4 3.2 7.1 5.7 11.3 7.4-.9 2.7-1.9 5.2-2.9 7.6zM113 3.6c0 8.1-3 15.7-8.9 22.7-7.1 8.3-15.7 13.1-25.1 12.4-.1-1-.2-2-.2-3.1 0-7.8 3.4-16.2 9.4-23 3-3.5 6.8-6.4 11.5-8.7 4.6-2.3 9-3.6 13.1-3.8.1 1.2.2 2.3.2 3.5z" />
  </svg>
);

const GoogleG = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z" />
    <path fill="#FBBC05" d="M10.5 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.3 0 11.8-2.1 15.7-5.7l-7.7-6c-2.1 1.4-4.8 2.3-8 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export default function MatrixLandingPage({ onSecretUnlock }) {
  const [tapCount, setTapCount] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const tapTimer = useRef(null);
  const videoRef = useRef(null);
  const { lang, toggleLang } = useLang();
  const { startCheckout, isActive } = useCheckout();
  const c = COPY[lang] || COPY.fr;
  const videoSrc = VIDEO_URL[lang] || VIDEO_URL.fr;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !videoOpen;
    video.play().catch(() => {});
  }, [videoOpen, videoSrc]);

  const openVideo = () => setVideoOpen(true);

  // Secret tap: click brand 5 times → prompt for access key
  const handleTitleTap = useCallback(() => {
    setTapCount(prev => {
      const next = prev + 1;
      if (next >= 5) {
        const key = window.prompt(c.prompt);
        if (key) {
          fetch(`${API}/api/auth/bypass`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key }),
          })
            .then(r => { if (!r.ok) throw new Error('fail'); return r.json(); })
            .then(data => {
              localStorage.setItem('token', data.access_token);
              localStorage.setItem('user', JSON.stringify(data.user));
              sessionStorage.setItem('openroom_unlocked', 'true');
              onSecretUnlock();
              window.location.href = '/dashboard';
            })
            .catch(() => { window.alert(c.invalid); });
        }
        return 0;
      }
      return next;
    });
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => setTapCount(0), 2000);
  }, [onSecretUnlock, c.prompt, c.invalid]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('key')) {
      // Key bypass handled by App.js via backend
      sessionStorage.setItem('openroom_unlocked', 'true');
      onSecretUnlock();
    }
  }, [onSecretUnlock]);

  return (
    <div data-testid="matrix-landing-page" className="ob-root">
      <div className="ob-shade" />

      <div className="ob-stage">

        {/* NAV */}
        <div className="ob-logo" onClick={handleTitleTap} data-testid="openroom-title-bypass">
          OpenBro<svg className="ob-logo-spark" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 0c.6 7.2 4.8 11.4 12 12-7.2.6-11.4 4.8-12 12-.6-7.2-4.8-11.4-12-12 7.2-.6 11.4-4.8 12-12z" /></svg>
            </div>
        <nav className="ob-nav-links">
          {c.nav.map((label) => (
            <button
              key={label}
              type="button"
              className="ob-nav-link"
              data-testid={`nav-${label.toLowerCase()}`}
              disabled={isActive}
              onClick={() => startCheckout(`nav-${label.toLowerCase()}`)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="ob-nav-right">
                <button
            type="button"
            className="ob-lang"
            data-testid="language-toggle"
            onClick={toggleLang}
            title={lang === 'fr' ? 'English' : 'Français'}
          >
            {lang === 'fr' ? 'EN' : 'FR'}
                </button>
          <a href="/login" className="ob-member" data-testid="landing-member-access">
            {c.member} <ArrowRight strokeWidth={2} />
          </a>
          <a href="/login" className="ob-user" aria-label={c.member}>
            <User strokeWidth={1.8} />
          </a>
        </div>

        {/* HERO */}
        <div className="ob-eyebrow">
          <strong>50</strong><i /><span>{c.eyebrow}</span>
        </div>
        <h1 className="ob-h1">OpenBro</h1>
        <p className="ob-tagline"><Lines text={c.tagline} /></p>
        <p className="ob-center-top"><Lines text={c.centerTop} /></p>
        <p className="ob-center-bottom"><Lines text={c.centerBottom} /></p>

        <ul className="ob-features">
          {c.features.map((label, i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <li key={label}>
                <span className="ob-feature-icon"><Icon strokeWidth={1.7} /></span>
                <span>{label}</span>
              </li>
            );
          })}
        </ul>

        <div className="ob-social">
          <div className="ob-avatars">
            {AVATARS.map((src) => (
              <img key={src} className="ob-avatar" src={src} alt="" />
            ))}
            </div>
          <div>
            <div className="ob-stars">★★★★★</div>
            <div className="ob-members">{c.members}</div>
          </div>
        </div>
        <p className="ob-values">
          {c.values.map((v, i) => (
            <React.Fragment key={v}>{i > 0 && <span>×</span>}{v}</React.Fragment>
          ))}
        </p>

        {/* CARD */}
        <div className="ob-flash ob-flash-card" />
        <div className="ob-flash ob-flash-portrait" />
        <div className="ob-card">
          <Rim id="rim-card" r={u(20)} />
          <p className="ob-kicker">{c.kicker}</p>
          <p className="ob-price"><strong>49€</strong> <span>{c.period}</span></p>
          <ul className="ob-perks">
            {c.perks.map((p) => (
              <li key={p}><i><Check strokeWidth={2.2} /></i><span>{p}</span></li>
            ))}
          </ul>
          <button
            type="button"
            className="ob-cta"
            data-testid="landing-cta-btn"
            disabled={isActive}
            onClick={() => startCheckout('landing-cta')}
          >
            {isActive ? '...' : c.cta} {!isActive && <ArrowRight strokeWidth={2.2} />}
          </button>
          <div className="ob-secure">
            <Lock strokeWidth={2} />
            <span className="ob-secure-label">{c.secure}</span>
            <span className="ob-pay ob-pay-mc" aria-label="Mastercard"><i /><i /></span>
            <span className="ob-pay ob-pay-visa">VISA</span>
            <span className="ob-pay ob-pay-apple" aria-label="Apple Pay"><AppleGlyph />Pay</span>
            <span className="ob-pay ob-pay-google" aria-label="Google Pay"><GoogleG />Pay</span>
          </div>
        </div>

        <div className="ob-portrait">
          <img src="/portrait-gaut.jpg" alt="Gaut Bonac" />
          <p><Lines text={c.quote} /></p>
          <Rim id="rim-portrait" r={u(18)} />
        </div>

        {/* FOOTER */}
        <button type="button" className="ob-video" onClick={openVideo}>
          <span className="ob-play"><Play strokeWidth={2} fill="#fff" /></span>
          <span>{c.video}</span>
        </button>
        <div className={videoOpen ? 'ob-player' : 'ob-thumb'} onClick={videoOpen ? undefined : openVideo} role="presentation">
          <video
            ref={videoRef}
            src={videoSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            controls={videoOpen}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
          {videoOpen && (
            <button type="button" className="ob-player-close" onClick={() => setVideoOpen(false)} aria-label="Fermer">×</button>
          )}
        </div>
        <p className="ob-accel"><Lines text={c.accel} /></p>
      </div>

      <style>{`
        .ob-root { --s: min(100vw / 1200, 100vh / 660); position: fixed; inset: 0; overflow-x: hidden; overflow-y: auto; display: flex; align-items: center; justify-content: center; background: #020409 url(/hero-earth.jpg) center / cover no-repeat; color: #fff; font-family: 'Inter', 'SF Pro Display', -apple-system, sans-serif; }
        .ob-root * { box-sizing: border-box; }
        .ob-shade { position: absolute; inset: 0; pointer-events: none; background: linear-gradient(90deg, rgba(2,4,9,0.34) 0%, rgba(2,4,9,0.14) 34%, rgba(2,4,9,0) 58%, rgba(2,4,9,0.08) 100%); }
        .ob-stage { position: relative; width: ${u(1024)}; height: ${u(576)}; flex: 0 0 auto; }
        .ob-stage > * { position: absolute; margin: 0; }
        .ob-stage svg { width: 1em; height: 1em; flex: 0 0 auto; }

        .ob-logo { left: ${u(38)}; top: ${u(17)}; display: inline-flex; align-items: flex-start; gap: ${u(1)}; font-size: ${u(23)}; line-height: 1.2; font-weight: 800; letter-spacing: -0.02em; cursor: default; user-select: none; text-shadow: 0 0 ${u(14)} rgba(255,255,255,0.25); }
        .ob-logo-spark { color: #fff; font-size: ${u(11)}; margin-top: ${u(-2)}; margin-left: ${u(1)}; filter: drop-shadow(0 0 ${u(4)} rgba(255,255,255,0.8)); }
        .ob-nav-links { left: ${u(278)}; top: ${u(21)}; display: flex; gap: 0; white-space: nowrap; }
        .ob-nav-link { font-family: inherit; font-size: ${u(7.5)}; font-weight: 500; letter-spacing: 0.14em; color: rgba(255,255,255,0.86); background: transparent; border: none; padding: ${u(7)} ${u(9.5)}; cursor: pointer; transition: color 0.15s; }
        .ob-nav-link:hover:not(:disabled) { color: #fff; }
        .ob-nav-link:disabled { cursor: wait; opacity: 0.6; }
        .ob-nav-right { right: ${u(32)}; top: ${u(17)}; display: flex; align-items: center; gap: ${u(12)}; }
        .ob-lang { font-family: inherit; font-size: ${u(7.5)}; font-weight: 600; letter-spacing: 0.14em; color: rgba(255,255,255,0.7); background: transparent; border: none; padding: 0; cursor: pointer; }
        .ob-lang:hover { color: #fff; }
        .ob-member { display: inline-flex; align-items: center; gap: ${u(8)}; height: ${u(27)}; padding: 0 ${u(15)}; font-size: ${u(8)}; font-weight: 600; letter-spacing: 0.14em; color: #fff; text-decoration: none; border: 1px solid rgba(255,255,255,0.7); border-radius: 999px; background: rgba(2,4,9,0.55); white-space: nowrap; box-shadow: 0 0 ${u(10)} rgba(255,255,255,0.28), inset 0 0 ${u(6)} rgba(255,255,255,0.08); }
        .ob-member svg { font-size: ${u(11)}; }
        .ob-member:hover { background: rgba(255,255,255,0.12); }
        .ob-user { width: ${u(27)}; height: ${u(27)}; border-radius: 50%; border: 1px solid rgba(255,255,255,0.7); display: grid; place-items: center; color: #fff; font-size: ${u(12)}; background: rgba(2,4,9,0.45); box-shadow: 0 0 ${u(8)} rgba(255,255,255,0.22); }

        .ob-eyebrow { left: ${u(38)}; top: ${u(88)}; display: flex; align-items: center; gap: ${u(10)}; white-space: nowrap; }
        .ob-eyebrow strong { font-size: ${u(14)}; font-weight: 800; letter-spacing: -0.02em; }
        .ob-eyebrow i { display: block; width: ${u(50)}; height: 1px; background: rgba(255,255,255,0.75); }
        .ob-eyebrow span { font-size: ${u(7.5)}; letter-spacing: 0.34em; color: rgba(255,255,255,0.7); }
        .ob-h1 { left: ${u(35)}; top: ${u(110)}; font-size: ${u(76)}; font-weight: 800; letter-spacing: -0.035em; line-height: 1; white-space: nowrap; background: linear-gradient(180deg, #ffffff 0%, #f2f5fa 40%, #c3ccdb 62%, #8d97ab 100%); -webkit-background-clip: text; background-clip: text; color: transparent; filter: drop-shadow(0 ${u(2)} ${u(10)} rgba(0,0,0,0.5)); }
        .ob-tagline { left: ${u(40)}; top: ${u(204)}; font-size: ${u(16)}; font-weight: 300; letter-spacing: 0.3em; line-height: 1.32; color: rgba(255,255,255,0.9); white-space: nowrap; }
        .ob-center-top { left: ${u(342)}; top: ${u(197)}; padding-left: ${u(14)}; border-left: 1px solid rgba(255,255,255,0.55); font-size: ${u(6.5)}; letter-spacing: 0.22em; line-height: 1.7; color: rgba(255,255,255,0.85); white-space: nowrap; }
        .ob-center-bottom { left: ${u(418)}; top: ${u(478)}; font-size: ${u(6.5)}; letter-spacing: 0.22em; line-height: 1.7; color: rgba(255,255,255,0.85); white-space: nowrap; }
        .ob-features { left: ${u(38)}; top: ${u(258)}; list-style: none; padding: 0; display: flex; flex-direction: column; gap: ${u(10)}; }
        .ob-features li { display: flex; align-items: center; gap: ${u(18)}; font-size: ${u(12.5)}; color: rgba(255,255,255,0.94); white-space: nowrap; }
        .ob-feature-icon { width: ${u(31)}; height: ${u(31)}; border-radius: 50%; display: grid; place-items: center; border: 1px solid rgba(255,255,255,0.55); color: #fff; font-size: ${u(13)}; background: radial-gradient(circle at 50% 30%, rgba(255,255,255,0.14), rgba(2,4,9,0.6)); box-shadow: 0 0 ${u(9)} rgba(200,215,255,0.35), inset 0 0 ${u(5)} rgba(255,255,255,0.12); }
        .ob-social { left: ${u(34)}; top: ${u(484)}; display: flex; align-items: center; gap: ${u(12)}; }
        .ob-avatars { display: flex; }
        .ob-avatar { width: ${u(36)}; height: ${u(36)}; border-radius: 50%; border: ${u(2)} solid #fff; margin-right: ${u(-7)}; display: block; object-fit: cover; box-shadow: 0 ${u(2)} ${u(6)} rgba(0,0,0,0.5); }
        .ob-stars { font-size: ${u(12)}; letter-spacing: 0.1em; color: #fff; line-height: 1; }
        .ob-members { font-size: ${u(11.5)}; color: #fff; margin-top: ${u(5)}; white-space: nowrap; }
        .ob-values { left: ${u(34)}; top: ${u(543)}; display: flex; align-items: center; gap: ${u(12)}; font-size: ${u(7)}; letter-spacing: 0.3em; color: rgba(255,255,255,0.8); white-space: nowrap; }
        .ob-values span { font-size: ${u(7)}; opacity: 0.7; }

        .ob-card { left: ${u(663)}; top: ${u(136)}; width: ${u(217)}; height: ${u(334)}; border-radius: ${u(20)}; padding: ${u(18)} ${u(16)} ${u(14)}; display: flex; flex-direction: column; background: radial-gradient(120% 60% at 0% 0%, rgba(120,135,165,0.35), rgba(120,135,165,0) 55%), linear-gradient(165deg, rgba(34,40,56,0.92) 0%, rgba(14,18,28,0.9) 45%, rgba(8,11,18,0.92) 100%); border: 1px solid rgba(255,255,255,0.14); box-shadow: 0 ${u(24)} ${u(60)} rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 0 ${u(24)} rgba(255,255,255,0.04); backdrop-filter: blur(24px) saturate(1.3); -webkit-backdrop-filter: blur(24px) saturate(1.3); }
        .ob-kicker { margin: 0 0 ${u(5)}; font-size: ${u(7.5)}; letter-spacing: 0.3em; color: rgba(255,255,255,0.78); white-space: nowrap; }
        .ob-price { margin: 0 0 ${u(13)}; display: flex; align-items: baseline; gap: ${u(7)}; }
        .ob-price strong { font-size: ${u(46)}; font-weight: 800; letter-spacing: -0.03em; line-height: 1.05; background: linear-gradient(180deg, #ffffff 0%, #dfe5ef 60%, #aeb8c9 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .ob-price span { font-size: ${u(15)}; color: rgba(255,255,255,0.8); }
        .ob-perks { list-style: none; margin: 0 0 ${u(16)}; padding: 0; display: flex; flex-direction: column; gap: ${u(8)}; }
        .ob-perks li { display: flex; align-items: center; gap: ${u(9)}; font-size: ${u(10.5)}; color: rgba(255,255,255,0.94); white-space: nowrap; }
        .ob-perks li i { width: ${u(14)}; height: ${u(14)}; border-radius: 50%; border: 1px solid rgba(255,255,255,0.8); display: grid; place-items: center; font-size: ${u(8.5)}; color: #fff; }
        .ob-cta { width: 100%; height: ${u(34)}; margin-top: auto; display: inline-flex; align-items: center; justify-content: center; gap: ${u(8)}; border-radius: 999px; cursor: pointer; background: linear-gradient(180deg, #ffffff, #eef1f6); color: #0a0d14; border: none; font-family: inherit; font-size: ${u(9.5)}; font-weight: 700; letter-spacing: 0.08em; white-space: nowrap; box-shadow: 0 0 ${u(14)} rgba(255,255,255,0.28), 0 ${u(8)} ${u(20)} rgba(0,0,0,0.45); }
        .ob-cta svg { font-size: ${u(13)}; }
        .ob-cta:hover:not(:disabled) { transform: translateY(-1px); }
        .ob-cta:disabled { cursor: wait; opacity: 0.8; }
        .ob-secure { display: flex; align-items: center; gap: ${u(5)}; margin-top: ${u(13)}; font-size: ${u(6.5)}; color: rgba(255,255,255,0.72); white-space: nowrap; overflow: hidden; }
        .ob-secure > svg { font-size: ${u(9)}; }
        .ob-secure-label { margin-right: ${u(4)}; }
        .ob-pay { display: inline-flex; align-items: center; gap: ${u(1)}; font-size: ${u(7.5)}; font-weight: 800; color: #fff; }
        .ob-pay-visa { font-style: italic; letter-spacing: 0.02em; font-size: ${u(8.5)}; }
        .ob-pay-mc i { width: ${u(9)}; height: ${u(9)}; border-radius: 50%; display: block; }
        .ob-pay-mc i:first-child { background: #eb001b; }
        .ob-pay-mc i:last-child { background: #f79e1b; margin-left: ${u(-4)}; opacity: 0.92; }
        .ob-pay-apple svg { font-size: ${u(8)}; margin-right: ${u(0.5)}; }
        .ob-pay-google svg { font-size: ${u(8)}; }
        .ob-pay-apple, .ob-pay-google { font-weight: 600; }

        .ob-portrait { left: ${u(888)}; top: ${u(118)}; width: ${u(122)}; height: ${u(352)}; border-radius: ${u(18)}; border: 1px solid rgba(255,255,255,0.14); background: #020409; box-shadow: 0 ${u(24)} ${u(60)} rgba(0,0,0,0.55); }
        .ob-stage svg.ob-rim { position: absolute; inset: -1px; width: calc(100% + 2px); height: calc(100% + 2px); overflow: visible; pointer-events: none; z-index: 2; filter: drop-shadow(0 0 ${u(3)} rgba(255,255,255,0.55)); }
        .ob-rim-rect { x: 0.75px; y: 0.75px; width: calc(100% - 1.5px); height: calc(100% - 1.5px); }
        .ob-flash { pointer-events: none; --m: ${u(30)}; background: radial-gradient(circle ${u(26)} at var(--m) var(--m), rgba(255,255,255,0.95), rgba(255,255,255,0.3) 45%, rgba(255,255,255,0) 100%), radial-gradient(circle ${u(20)} at var(--m) calc(100% - var(--m)), rgba(255,255,255,0.8), rgba(255,255,255,0.25) 45%, rgba(255,255,255,0) 100%), radial-gradient(circle ${u(24)} at calc(100% - var(--m)) calc(100% - var(--m)), rgba(255,255,255,0.9), rgba(255,255,255,0.3) 45%, rgba(255,255,255,0) 100%); }
        .ob-flash-card { left: calc(${u(663)} - var(--m)); top: calc(${u(136)} - var(--m)); width: calc(${u(217)} + 2 * var(--m)); height: calc(${u(334)} + 2 * var(--m)); }
        .ob-flash-portrait { left: calc(${u(888)} - var(--m)); top: calc(${u(118)} - var(--m)); width: calc(${u(122)} + 2 * var(--m)); height: calc(${u(352)} + 2 * var(--m)); }
        .ob-portrait img { width: 100%; height: 100%; object-fit: cover; object-position: center top; display: block; border-radius: inherit; }
        .ob-portrait p { position: absolute; left: 0; right: 0; bottom: 0; margin: 0; border-radius: 0 0 ${u(18)} ${u(18)}; padding: ${u(40)} ${u(12)} ${u(22)}; font-size: ${u(7.5)}; letter-spacing: 0.18em; line-height: 1.85; color: #fff; background: linear-gradient(180deg, rgba(2,4,9,0) 0%, rgba(2,4,9,0.9) 60%); }

        .ob-video { left: ${u(700)}; top: ${u(511)}; display: inline-flex; align-items: center; gap: ${u(12)}; text-decoration: none; color: rgba(255,255,255,0.92); font-size: ${u(8)}; letter-spacing: 0.24em; white-space: nowrap; background: none; border: none; padding: 0; font-family: inherit; cursor: pointer; }
        .ob-play { width: ${u(25)}; height: ${u(25)}; border-radius: 50%; border: 1px solid rgba(255,255,255,0.85); display: grid; place-items: center; font-size: ${u(9)}; box-shadow: 0 0 ${u(8)} rgba(255,255,255,0.25); }
        .ob-video:hover { color: #fff; }
        .ob-thumb { left: ${u(816)}; top: ${u(501)}; width: ${u(72)}; height: ${u(45)}; border-radius: ${u(6)}; overflow: hidden; border: 1px solid rgba(255,255,255,0.4); display: block; box-shadow: 0 ${u(6)} ${u(16)} rgba(0,0,0,0.5); background: none; padding: 0; cursor: pointer; }
        .ob-thumb video { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; }
        .ob-player { left: 50%; top: 50%; transform: translate(-50%, -50%); width: ${u(340)}; height: ${u(340)}; z-index: 6; border-radius: ${u(20)}; overflow: hidden; background: #020409; border: 1px solid rgba(255,255,255,0.22); box-shadow: 0 ${u(28)} ${u(80)} rgba(0,0,0,0.72), inset 0 1px 0 rgba(255,255,255,0.22); }
        .ob-player video { width: 100%; height: 100%; object-fit: contain; background: #020409; display: block; }
        .ob-player-close { position: absolute; top: ${u(8)}; right: ${u(8)}; z-index: 2; width: ${u(22)}; height: ${u(22)}; border-radius: 50%; border: 1px solid rgba(255,255,255,0.45); background: rgba(2,4,9,0.72); color: #fff; font-size: ${u(14)}; line-height: 1; cursor: pointer; }
        .ob-accel { left: ${u(905)}; top: ${u(505)}; padding-left: ${u(12)}; border-left: 1px solid rgba(255,255,255,0.5); font-size: ${u(6.5)}; letter-spacing: 0.22em; line-height: 1.85; color: rgba(255,255,255,0.88); white-space: nowrap; }

        @media (max-width: 820px) {
          .ob-root { --s: calc(100vw / 420); align-items: flex-start; }
          .ob-stage { width: 100vw; height: auto; padding: ${u(16)} 6% ${u(28)}; display: flex; flex-direction: column; }
          .ob-stage > * { position: static; }
          .ob-nav-links, .ob-portrait, .ob-flash, .ob-center-top, .ob-center-bottom, .ob-eyebrow, .ob-values, .ob-thumb, .ob-accel, .ob-user { display: none; }
          .ob-logo { order: 1; }
          .ob-nav-right { order: 1; position: absolute; right: 6%; top: ${u(14)}; }
          .ob-nav-right .ob-member { display: none; }
          .ob-h1 { order: 2; font-size: ${u(64)}; margin-top: ${u(48)}; }
          .ob-tagline { order: 3; font-size: ${u(14)}; margin-top: ${u(10)}; }
          .ob-features { order: 4; margin-top: ${u(24)}; }
          .ob-social { order: 5; margin-top: ${u(24)}; }
          .ob-card { order: 6; width: 100%; height: auto; margin-top: ${u(28)}; padding: ${u(22)} ${u(20)} ${u(18)}; }
          .ob-card .ob-perks li, .ob-card .ob-kicker { font-size: ${u(11)}; }
          .ob-cta { height: ${u(44)}; font-size: ${u(11)}; }
          .ob-video { order: 7; margin-top: ${u(20)}; align-self: flex-start; }
          .ob-player { order: 8; position: relative; left: auto; top: auto; transform: none; width: min(88vw, ${u(340)}); height: auto; aspect-ratio: 1; margin: ${u(16)} auto 0; }
        }
      `}</style>
    </div>
  );
}
